import { Box, Button, Card, Tab, Tabs, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import { useNavigate, useParams } from 'react-router-dom';
import { enqueueSnackbar, closeSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import DoDisturbAltOutlinedIcon from '@mui/icons-material/DoDisturbAltOutlined';
import UserDetail from './components/UserDetail';
import {
	useDeleteUserMutation,
	useFreezeUserMutation,
	useGetUserDetailQuery,
	useUnfreezeUserMutation,
} from '@/api/user/userApiSlice';
import ChangePasswordModal from './components/ChangePasswordModal';
import User330Detail from './components/User330Detail';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import UserExerciseProgress from './components/UserExerciseProgress';
import UserTreasureSummaryPage from './components/UserTreasureSummaryPage';

export default function UserInfoPage() {
	const { t } = useTranslation();
	const { id } = useParams();
	const [freezeUser] = useFreezeUserMutation();
	const [unfreezeUser] = useUnfreezeUserMutation();
	const navigate = useNavigate();
	const showModal = useModal();
	const [currentTab, setCurrentTab] = useState('userDetail');
	const [deleteUser] = useDeleteUserMutation();

	const { data: userDetail }: any = useGetUserDetailQuery(id || '', {
		refetchOnMountOrArgChange: true,
	});

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};
	const handleDelete = () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('user.deleteUser')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>{t('user.isDeleteUser')}</Typography>
				}
				cancellationText=''
				confirmationText={t('button.delete')}
				confirmationButtonProps={{ color: 'error', variant: 'outlined' }}
				onConfirm={async () => {
					const response = await deleteUser(id || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deleteUserSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
						navigate('/user-manage/user-list');
					}
				}}
			/>
		));
	};

	const handleFreeze = () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('user.freezeUser')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>{t('user.isFreezeUser')}</Typography>
				}
				cancellationText=''
				confirmationText={t('button.confirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await freezeUser(id || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.freezeUserSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
					}
				}}
			/>
		));
	};
	const handleUnFreeze = () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('user.unfreezeUser')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>{t('user.isUnFreezeUser')}</Typography>
				}
				cancellationText=''
				confirmationText={t('button.confirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await unfreezeUser(id || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.unfreezeUserSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
					}
				}}
			/>
		));
	};
	return (
		<>
			<Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
				<Box sx={{ width: '100%' }}>
					<Box display='flex' justifyContent='space-between'>
						<Typography variant='h4'>
							{t('user.userInfo')}({userDetail?.data.username},
							{userDetail?.data.nickname})
						</Typography>
						<Box>
							<Button
								endIcon={<DeleteOutlined />}
								onClick={handleDelete}
								sx={{ marginRight: '24px' }}
								variant='contained'
							>
								{t('button.delete')}
							</Button>
							<ChangePasswordModal />
							{userDetail?.data.userStatus === 0 && (
								<Button
									endIcon={<DoDisturbAltOutlinedIcon />}
									onClick={handleFreeze}
									variant='contained'
								>
									{t('button.freeze')}
								</Button>
							)}
						</Box>
					</Box>
					{userDetail?.data.userStatus === 1 && (
						<Box
							display='flex'
							justifyContent='space-between'
							alignItems={'center'}
							sx={{
								backgroundColor: '#EBEFFF',
								color: '#1D2F99',
								padding: '6px 12px',
								borderRadius: '8px',
								marginTop: '24px',
							}}
						>
							<Typography
								sx={{
									display: 'flex',
								}}
							>
								<ErrorOutlineIcon />
								&nbsp;
								{t('user.freezedUser')}
							</Typography>
							<Button
								onClick={handleUnFreeze}
								// variant='contained'
							>
								<span style={{ textDecoration: 'underline' }}>
									{t('button.unfreeze')}
								</span>
							</Button>
						</Box>
					)}
					<Card sx={{ marginTop: 3 }}>
						<Tabs
							value={currentTab}
							sx={{ marginTop: '24px', marginLeft: '24px' }}
							onChange={handleTabChange}
						>
							<Tab
								label={
									<Typography variant='body1'>
										{t('user.userDetail')}
									</Typography>
								}
								value='userDetail'
							/>
							<Tab
								label={
									<Typography variant='body1'>{t('user.330Detail')}</Typography>
								}
								value='330Detail'
							/>
							<Tab
								label={
									<Typography variant='body1'>
										{t('user.userExerciseProgress')}
									</Typography>
								}
								value='progress'
							/>
							<Tab
								label={
									<Typography variant='body1'>
										{t('user.userTreasure')}
									</Typography>
								}
								value='treasure'
							/>
						</Tabs>
						{currentTab == 'userDetail' && (
							<UserDetail userDetail={userDetail?.data || {}}></UserDetail>
						)}
						{currentTab == '330Detail' && <User330Detail></User330Detail>}
						{currentTab == 'progress' && (
							<UserExerciseProgress></UserExerciseProgress>
						)}
						{currentTab == 'treasure' && (
							<UserTreasureSummaryPage></UserTreasureSummaryPage>
						)}
					</Card>
				</Box>
			</Box>
		</>
	);
}
